import React, { useEffect, useState, useMemo, useCallback, useRef, forwardRef } from "react";
import { useForm, useWatch } from 'react-hook-form';
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  TableContainer,
  TableHead,
  TableRow,
  Table,
  TableBody,
  IconButton,
  Box,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import localizationKeys from "../../../i18n/localizationKeys.js";
import {
  CHART_PROMPTS,
  DEFAULT_CHART_TITLE,
  MAX_CHANNEL_COUNT,
} from "../../../constants/trendGraph/charts";
import { updateCharts, deleteChart } from "../../../api/charts";
import { Confirm } from "./modal/confirm.jsx";
import { DigitalChVarSelect } from "./modal/digitalChVarSelect.jsx";
import DeleteIcon from "../icon/deleteIcon.jsx";
import { GRAPH_TYPE } from "../../../constants/trendGraph/trendGraphType.js";
import { mockData } from "./data/mockData.js";
import { mockDataAvg } from "./data/mockDataAvg.js"
import { DEFAULT_LINE_COLORS } from "../../../constants/trendGraph/linesDefaultColors.js";
import { Chart } from "../../chart/chart.jsx";
import { useNavigate } from "react-router-dom";

import PencilIcon from "../../../../assets/images/custom/pencil-alt.svg";
import { CHANNEL_TYPE, isDigitalChannel, isDisplayedOnCustom } from "../../../constants/channel/config.js";
import { TableVirtuoso } from "react-virtuoso";
import { ResetIcon } from "../../alarmPage/resetIcon.jsx";
import { TableCustBody, TableHeader } from "./virtualScrollTableComponent.jsx";
import { customChannelSelectorStyles } from "./virtualScrollTableStyle.js";
import { DisplaySelectedChannel } from "./displaySelectedChannel.jsx";

const filterChannels = (channels, searchText = "") => {
  const filtered = channels.filter(ch => isDisplayedOnCustom(ch.chType));
  if (searchText === "" || searchText == null)
    return filtered;
  const loweredSearchText = searchText.toLowerCase();
  return filtered.filter((ch) => ch.chName?.toLowerCase().includes(loweredSearchText) || ch.chNo.toString().includes(loweredSearchText));
}
export const CustomGraph = (props) => {
  const {
    vesselId,
    currentVessel,
    customChartTitle = useWatch({ control, name: 'title' }),
    setCustomChartTitle,
    setIsCustomChartsCreated,
    setIsCreateCustomCharts,
    setIsChartDeleted,
    customCharts,
    selectedChartData,
    setSelectedChartData,
    channels,
  } = props;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { register, setValue, handleSubmit, reset, control } = useForm({
    defaultValues: {
      title: customChartTitle,
    },
    mode: "onBlur",
  });
  const [filteredChannels, setFilteredChannels] = useState(filterChannels(channels));
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptContent, setPromptContent] = useState({});
  const [showVarSelect, setShowVarSelect] = useState(false);
  const [varOptions, setVarOptions] = useState([]);
  const [selectedVar, setSelectedVar] = useState("var1");
  const [currChannel, setCurrChannel] = useState({});
  const [selectedChannelsDigital, setSelectedChannelsDigital] = useState([]);
  const [subtitle, setSubtitle] = useState("");

  const VirtuosoTableComponents = {
    Table: (props) => (<Table stickyHeader {...props} />),
    TableHead: forwardRef((props, ref) => (<TableHead {...props} ref={ref} />)),
    TableRow,
    TableBody: forwardRef((props, ref) => (<TableBody {...props} ref={ref} />)),
  }

  const language = useCallback(() => {
    if (i18n.language === "ja") return "EN";
    else if (i18n.language === "en-US") return "JP";
    else return "";
  }, [i18n.language]);

  const chartLines = useMemo(() => {
    return (
      //selectedChannels?.filter(line => selectedChannelsDigital.some(ch => ch.chNo != line))?.map((line, idx) => {
      selectedChannels?.map((line, idx) => {
        return {
          key: `"${line}"`,
          keyName: `"${line}"`,
          name: `"${line}"`,
          color: DEFAULT_LINE_COLORS[idx],
          enableVisibilityIcon: "hidden",
          enable: true,
          channelType: selectedChannelsDigital?.find(ch => ch.chNo == line) === undefined ? CHANNEL_TYPE.ANALOG : CHANNEL_TYPE.DIGITAL,
          digitalVar: selectedChannelsDigital?.find(ch => ch.chNo == line)?.var || null,
        };
      }) ?? null
    );
  }, [selectedChannels, selectedChannelsDigital]);

  const mockValues = useMemo(() => {
    if (selectedChannelsDigital?.length == selectedChannels?.length) return [];
    const data = [];
    mockData.forEach((d) => {
      const chartData = {};
      chartLines.filter(line => line.channelType === CHANNEL_TYPE.ANALOG).forEach((line, idx) => (chartData[line.key] = d[idx + 1]));
      data.push({
        dateTime: d.dateTime,
        ...chartData,
      });
    });
    return data;
  }, [chartLines]);

  const mockValuesDigital = useMemo(() => {
    if (selectedChannelsDigital?.length <= 0) return [];
    const data = [];
    mockData.forEach((d) => {
      const chartData = {};
      chartLines
        .filter(line => line.channelType === CHANNEL_TYPE.DIGITAL)
        .forEach((line, idx) => (chartData[line.key] = d[idx + 1] >= mockDataAvg[idx + 1] ? line.digitalVar : ""));
      data.push({
        dateTime: d.dateTime,
        ...chartData,
      });
    });
    return data;
  }, [chartLines]);

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, []);

  useEffect(() => {
    if (channels?.length > 0 && channels[0].vesselId === vesselId) {
      if (selectedChartData) {
        setValue("title", selectedChartData.name);
        setValue("subtitle", selectedChartData.otherName);
        setCustomChartTitle(selectedChartData.name);
        setSubtitle(selectedChartData.otherName);
        const sel = selectedChartData.lines.map((ch) => {
          const validChannel = channels.find((c) => c.chNo === ch.chNo);
          if(validChannel) {
            validChannel.selected = true;
          }
          return validChannel?.chNo;
        }).filter((e) => e !== undefined);
        setSelectedChannels(sel);
        const selDigital = selectedChartData.lines.filter(ch => ch?.digitalVar !== undefined && ch?.digitalVar !== null).map((ch) => {
          const validChannel = channels.find((c) => c.chNo === ch.chNo);
          if(validChannel) {
            validChannel.selected = true;
          }
          return validChannel?.chNo;
        }).filter((e) => e !== undefined);
        setSelectedChannelsDigital(selDigital);
      } else {
        resetForm();
      }
    }
  }, [channels, selectedChartData]);

  const updateSelectedChannels = (value, chType, isChecked) => {
    const ch = channels.find((c) => c.chNo === value);
    if (isChecked) {
      if (isDigitalChannel(chType)) {
        const statusList = ch?.status?.split(",");
        setShowVarSelect(true);
        setSelectedVar(statusList?.length === 2 ? statusList[0] : "Var1");
        setVarOptions(statusList?.length === 2 ? statusList : ["Var1", "Var2"]);
        setCurrChannel(ch);
      }
      else {
        setSelectedChannels([...new Set([...selectedChannels, value])]);
        ch.selected = true;
      }
    } else {
      setSelectedChannels([...selectedChannels.filter((ch) => ch !== value)]);
      setSelectedChannelsDigital([...selectedChannelsDigital.filter((ch) => ch.chNo !== value)]);
      ch.selected = false;
    }
  };

  const varSelectConfirmed = () => {
    const ch = currChannel;
    ch.selected = true;
    setCurrChannel(ch);
    setSelectedChannels([...new Set([...selectedChannels, currChannel.chNo])]);
    setSelectedChannelsDigital([...new Set([...selectedChannelsDigital, { chNo: currChannel.chNo, var: selectedVar }])]);

    setShowVarSelect(false);
    setSelectedVar("");
    setCurrChannel({});
  }

  const varSelectCanceled = () => {
    setShowVarSelect(false);
    setSelectedVar("");
    setCurrChannel({});
    updateSelectedChannels(currChannel.chNo, "", false)
  }

  const isSubmitDisabled = useMemo(() => {
    const selectedChannelsLength = selectedChannels?.length ?? 0;
    return selectedChannelsLength === 0;
  });

  const updateSearchValue = (value) => {
    setSearchValue(value);
    setFilteredChannels(filterChannels(channels, value));
  };

  const resetForm = () => {
    setCustomChartTitle(DEFAULT_CHART_TITLE);
    setSubtitle("");
    setSearchValue("");
    setFilteredChannels([]);
    setSelectedChannels([]);
    setSelectedChannelsDigital([]);
    reset({
      title: DEFAULT_CHART_TITLE,
      subtitle: "",
      selectedChannels: [],
      selectedChannelsDigital: [],
    });
    channels.map((ch) => (ch.selected = false));
    setFilteredChannels(filterChannels(channels));
  };

  const saveChart = async (data) => {
    const req = {
      id: selectedChartData?.id,
      vesselId,
      name: data.title,
      otherName: data.subtitle,
      position: (customCharts?.length ?? 0) + 1,
      lines: selectedChannels.map((ch) => ({
        chNo: ch,
        digitalVar: (selectedChannelsDigital.find(c => c.chNo == ch))?.var || null,
      })),
    };

    await updateCharts(req);
    setIsCustomChartsCreated(true);
    setSelectedChartData(req)
  };

  const showAll = () => {
    setSearchValue("");
    setFilteredChannels(filterChannels(channels));
  };

  const onCancel = () => {
    const okHandler = () => {
      if (selectedChartData) {
        setIsCreateCustomCharts(false);
      } else if (customCharts?.length > 0) {
        resetForm();
        navigate(`/trend/custom/${customCharts[0].id}/${vesselId}`);
      } else {
        resetForm();
      }
      setShowPrompt(false);
    };

    const cancelHandler = () => {
      setShowPrompt(false);
    };

    setPromptContent({ ...CHART_PROMPTS.CANCEL, okHandler, cancelHandler });
    setShowPrompt(true);
  };

  const onSubmit = async (data) => {
    const submitData = data;

    const okHandler = () => {
      if (selectedChartData) setIsCreateCustomCharts(false);
      saveChart(submitData);
      setShowPrompt(false);
    };

    const cancelHandler = () => {
      setShowPrompt(false);
    };

    setPromptContent({ ...CHART_PROMPTS.SAVE, okHandler, cancelHandler });
    setShowPrompt(true);
  };

  const onDelete = async () => {
    const okHandler = async () => {
      await deleteChart(vesselId, selectedChartData.id);
      setIsChartDeleted(true);
      setShowPrompt(false);
    };

    const cancelHandler = () => {
      setShowPrompt(false);
    };

    setPromptContent({
      ...CHART_PROMPTS.DELETE,
      okHandler,
      cancelHandler,
      isDelete: true,
    });
    setShowPrompt(true);
  };

  const titleInputRef = useRef(null);

  const handlePencilIconClick = () => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
    }
  };
  return (
    <Grid container sx={{ width: "100%" }}>
      <Confirm
        when={showPrompt}
        title={t(promptContent.title)}
        message={t(promptContent.message)}
        cancelText={t(promptContent.cancelText)}
        okText={t(promptContent.okText)}
        onOK={promptContent.okHandler}
        onCancel={promptContent.cancelHandler}
        isDelete={promptContent.isDelete}
      />
      <DigitalChVarSelect
        when={showVarSelect}
        onOK={varSelectConfirmed}
        onCancel={varSelectCanceled}
        selectedVar={selectedVar}
        setSelectedVar={setSelectedVar}
        varOptions={varOptions}
        chName={currChannel?.chName}
        chNo={currChannel?.chNo}
      />
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={customChannelSelectorStyles.form}>
        <Grid item size={{ xs: 12 }} sx={customChannelSelectorStyles.titleContainer}>
          <Box sx={customChannelSelectorStyles.chartTitle} onClick={handlePencilIconClick}>
            <input
              value={customChartTitle}
              {...register("title", {
                onChange: (event) => {
                  setCustomChartTitle(event.target.value);
                  setValue("title", event.target.value);
                },
                required: true,
              })}
              size={60}
              maxLength={31}
              ref={titleInputRef}
            />
            <Box sx={customChannelSelectorStyles.titleIcon}>
              <Box component={"img"} src={PencilIcon} width={19} height={20} sx={{ cursor: 'pointer' }} />
            </Box>
          </Box>
        </Grid>
        <Grid item size={{ xs: 12 }} sx={customChannelSelectorStyles.chartSubtitle}>
          <div>{language()}</div>
          <Box sx={{ width: '100%' }}>
            <input
              value={subtitle}
              {...register("subtitle", {
                onChange: (event) => setSubtitle(event.target.value),
              })}
              placeholder={t(localizationKeys.CustomSubtitle)}
              size="38"
            />
          </Box>
        </Grid>
        <Grid container sx={customChannelSelectorStyles.body} spacing={2}>
          <Grid item size={{ xs: 6 }}>
            <Box sx={customChannelSelectorStyles.searchControl}>
              <div>{t(localizationKeys.CustomSearchChannels)}</div>
              <div>
                <Box
                  component="input"
                  onChange={(evt) => updateSearchValue(evt.target.value)}
                  value={searchValue}
                  placeholder={t(localizationKeys.CustomSearchChannelsPlaceholder)}
                  size="50"
                  sx={customChannelSelectorStyles.searchInput}
                />
              </div>
              <Box sx={customChannelSelectorStyles.showAll}>
                {searchValue.length > 0 &&
                  <Box onClick={showAll} sx={{ display: 'flex', alignItems: 'center' }}><ResetIcon color={"#19B2AB"} /><span>{t(localizationKeys.ResetSearch_lower)}</span></Box>
                }
              </Box>
            </Box>
            <Box sx={customChannelSelectorStyles.tableContainer}>
              {filteredChannels?.length ? (
                <TableContainer sx={customChannelSelectorStyles.table}>
                  <TableVirtuoso
                    style={{ width: "100%", height: "100%" }}
                    totalCount={filteredChannels.length}
                    data={filteredChannels}
                    components={VirtuosoTableComponents}
                    fixedHeaderContent={TableHeader}
                    itemContent={(_index, row) => (
                      TableCustBody(selectedChannels, row, updateSelectedChannels)
                    )}
                  />
                </TableContainer>
              ) : (
                <div>
                  <Box sx={customChannelSelectorStyles.tableMessage}>
                    {t(localizationKeys.CustomChartCountNote)}
                  </Box>
                  <Box sx={customChannelSelectorStyles.noMatchFound}>
                    <span>{t(localizationKeys.CustomNoMatchFound)}</span>
                  </Box>
                </div>
              )}
            </Box>
          </Grid>
          <Grid item size={{ xs: 6 }}>
            <Box sx={customChannelSelectorStyles.graphPreview}>
              <div>
                <Box component="span" sx={customChannelSelectorStyles.graphHeader}>
                  {selectedChannels.length === MAX_CHANNEL_COUNT
                    ? t(localizationKeys.CustomGraphPreviewFull)
                    : t(localizationKeys.CustomGraphPreview)}
                </Box>
                <Box component="span" sx={customChannelSelectorStyles.graphSubHeader}>
                  {selectedChannels.length === MAX_CHANNEL_COUNT
                    ? t(localizationKeys.CustomVariableNoteFull)
                    : t(localizationKeys.CustomVariableNote)}
                </Box>
              </div>
              <Box sx={customChannelSelectorStyles.graph}>
                <Chart
                  chartType={"preview"}
                  graphData={mockValues}
                  graphDataDigital={mockValuesDigital}
                  customChartLines={chartLines}
                  vesselId={vesselId}
                  type={GRAPH_TYPE.CUSTOM}
                  isInitialLoad={false}
                  isGraphDataLoading={false}
                  isMockPreview={true}
                  vessel={currentVessel}
                />
              </Box>
              <Box component="p" sx={customChannelSelectorStyles.customChartDisclaimer}>
                This is an example graph and data curves may not reflect reality.
              </Box>
            </Box>
            <Box sx={customChannelSelectorStyles.linesContainer}>
              <Box sx={customChannelSelectorStyles.lines}>{selectedChannels?.map((chNo) => 
                <DisplaySelectedChannel key={chNo} channels={channels} chNo={chNo} selectedChannelsDigital={selectedChannelsDigital} updateSelectedChannels={updateSelectedChannels} />
                )}
              </Box>
            </Box>
            <Box sx={customChannelSelectorStyles.controller}>
              <Box sx={customChannelSelectorStyles.controllerLeft}>
                <Box
                  component="input"
                  type="submit"
                  sx={isSubmitDisabled ? customChannelSelectorStyles.disabledSubmitButton : customChannelSelectorStyles.submitButton}
                  disabled={isSubmitDisabled}
                  value={t(localizationKeys.CustomSaveGraph)}
                />
                <Box component="span" sx={customChannelSelectorStyles.cancelButton} onClick={onCancel}>
                  {t(localizationKeys.CancelButtonText)}
                </Box>
              </Box>
              {selectedChartData && (
                <Box sx={customChannelSelectorStyles.controllerRight}>
                  <IconButton onClick={onDelete} size="large">
                    <DeleteIcon />
                    <Box component="span" sx={customChannelSelectorStyles.deleteButton}>{t(localizationKeys.CustomDelete)}</Box>
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Grid>
  );
};

CustomGraph.propTypes = {
  currentVessel: PropTypes.object,
  customChartTitle: PropTypes.string,
  setCustomChartTitle: PropTypes.func,
  setIsCustomChartsCreated: PropTypes.func,
  setIsCreateCustomCharts: PropTypes.func,
  setIsChartDeleted: PropTypes.func,
  customCharts: PropTypes.object,
  selectedChartData: PropTypes.object,
  setSelectedChartData: PropTypes.func,
  channels: PropTypes.array,
  graph: PropTypes.string,
  vesselId: PropTypes.string,
};

